/**
 * Algolia Search Results Web Component
 * Full search results page with faceted filters using InstantSearch.js
 */

class AlgoliaSearchResults extends HTMLElement {
  constructor() {
    super();
    this.search = null;
    // Hide filters by default on mobile (under 990px)
    this.filtersVisible = window.innerWidth >= 990;
    this.activeProductType = null; // Track active product type for tabs
    this.activeSearchQuery = null; // Track search query for header updates
    this.searchMode = null; // 'all_resources' or 'sermons'
    this.activeCategoryValue = null; // For sermon mode tabs
    this.categoryRefine = null; // Refine function for category widget
    this.subCategoryRefine = null; // Refine function for subcategory widget
    this.activeSubCategoryValue = null; // Track active subcategory
    this.subCategoryItems = []; // Store subcategory items from Algolia
    this.activeTopicValue = null; // Track active topic for tabs
    this.topicRefine = null; // Refine function for topic widget
    this.activeFilterType = null; // Track which filter type is active ('category', 'topic', or null)
    // Store all facet values from first load (for showing 0-count items as disabled)
    this.allCategoryValues = null;
    this.allSubCategoryValues = null;
    this.allTopicValues = null;
    // Store last rendered state signature to prevent unnecessary re-renders
    this.lastCategorySignature = null;
    this.lastTopicSignature = null;
  }

  // Header content for each product type (when no search query)
  static HEADER_CONTENT = {
    '': {
      title: 'All Resources',
      description: 'Browse our complete library of sermon series, illustrations, graphics, and videos.'
    },
    'Sermon Series': {
      title: 'Sermon Series',
      description: 'Explore topical, expository, lectionary and holiday sermon series.'
    },
    'Illustration': {
      title: 'Illustrations',
      description: 'Discover compelling illustrations to enhance your sermons and teachings.'
    },
    'Announcement': {
      title: 'Graphics',
      description: 'Find announcement slides, social media graphics, and visual assets for your ministry.'
    },
    'Video': {
      title: 'Videos',
      description: 'Browse video content for worship, announcements, and sermon support.'
    }
  };

  // Header content for sermon mode (when no search query)
  static SERMON_HEADER_CONTENT = {
    '': {
      title: 'Sermon Series',
      description: 'Explore our complete library of topical, expository, and holiday sermon series.'
    },
    'Topical': {
      title: 'Topical Sermon Series',
      description: 'Timely series that speak to the pressing needs of your congregation.'
    },
    'Bible Book/Expository': {
      title: 'Expository Sermon Series',
      description: 'Dive deep into Scripture with series that unpack each book of the Bible.'
    },
    'Holidays & Seasons': {
      title: 'Holiday Sermon Series',
      description: 'Sermon series for Christmas, Easter, and other special occasions.'
    }
  };

  // Header content for illustrations mode
  static ILLUSTRATIONS_HEADER_CONTENT = {
    '': {
      title: 'Illustrations',
      description: 'Discover compelling illustrations to enhance your sermons and teachings.'
    },
    'PLACEHOLDER_1': {
      title: 'Category 1 Illustrations',
      description: 'Browse Category 1 illustrations.'
    },
    'PLACEHOLDER_2': {
      title: 'Category 2 Illustrations',
      description: 'Browse Category 2 illustrations.'
    },
    'PLACEHOLDER_3': {
      title: 'Category 3 Illustrations',
      description: 'Browse Category 3 illustrations.'
    }
  };

  // Header content for graphics mode
  static GRAPHICS_HEADER_CONTENT = {
    '': {
      title: 'Graphics',
      description: 'Find announcement slides, social media graphics, and visual assets for your ministry.'
    },
    'PLACEHOLDER_1': {
      title: 'Category 1 Graphics',
      description: 'Browse Category 1 graphics.'
    },
    'PLACEHOLDER_2': {
      title: 'Category 2 Graphics',
      description: 'Browse Category 2 graphics.'
    },
    'PLACEHOLDER_3': {
      title: 'Category 3 Graphics',
      description: 'Browse Category 3 graphics.'
    }
  };

  // Header content for videos mode
  static VIDEOS_HEADER_CONTENT = {
    '': {
      title: 'Videos',
      description: 'Browse video content for worship, announcements, and sermon support.'
    },
    'PLACEHOLDER_1': {
      title: 'Category 1 Videos',
      description: 'Browse Category 1 videos.'
    },
    'PLACEHOLDER_2': {
      title: 'Category 2 Videos',
      description: 'Browse Category 2 videos.'
    },
    'PLACEHOLDER_3': {
      title: 'Category 3 Videos',
      description: 'Browse Category 3 videos.'
    }
  };

  // Header content for social mode
  static SOCIAL_HEADER_CONTENT = {
    '': {
      title: 'Social',
      description: 'Browse social media graphics and content for your ministry.'
    },
    'PLACEHOLDER_1': {
      title: 'Category 1 Social',
      description: 'Browse Category 1 social content.'
    },
    'PLACEHOLDER_2': {
      title: 'Category 2 Social',
      description: 'Browse Category 2 social content.'
    },
    'PLACEHOLDER_3': {
      title: 'Category 3 Social',
      description: 'Browse Category 3 social content.'
    }
  };

  // Header content for docs-forms mode
  static DOCS_FORMS_HEADER_CONTENT = {
    '': {
      title: 'Docs & Forms',
      description: 'Browse documents and forms for church administration.'
    },
    'PLACEHOLDER_1': {
      title: 'Category 1 Docs & Forms',
      description: 'Browse Category 1 documents and forms.'
    },
    'PLACEHOLDER_2': {
      title: 'Category 2 Docs & Forms',
      description: 'Browse Category 2 documents and forms.'
    },
    'PLACEHOLDER_3': {
      title: 'Category 3 Docs & Forms',
      description: 'Browse Category 3 documents and forms.'
    }
  };

  // Header content for bible-guides mode
  static BIBLE_GUIDES_HEADER_CONTENT = {
    '': {
      title: 'Bible Book Guides',
      description: 'Comprehensive guides to help you study and teach through books of the Bible.'
    },
    'PLACEHOLDER_1': {
      title: 'Category 1 Bible Guides',
      description: 'Browse Category 1 Bible guides.'
    },
    'PLACEHOLDER_2': {
      title: 'Category 2 Bible Guides',
      description: 'Browse Category 2 Bible guides.'
    },
    'PLACEHOLDER_3': {
      title: 'Category 3 Bible Guides',
      description: 'Browse Category 3 Bible guides.'
    }
  };

  // Header content for sermon-calendars mode
  static SERMON_CALENDARS_HEADER_CONTENT = {
    '': {
      title: 'Sermon Calendars',
      description: 'Plan your preaching schedule with our curated sermon calendars.'
    }
  };

  // Product type → search page URL mapping (for cross-type "See more" links)
  static PRODUCT_TYPE_PAGE_URLS = {
    'Sermon Series': '/pages/products-sermon-series',
    'Illustration': '/pages/products-sermon-illustrations',
    'Announcement': '/pages/products-graphics',
    'Video': '/pages/products-videos',
    'Social Calendar': '/pages/products-social',
    'Docs & Forms': '/pages/products-docs-forms',
    'Bible Guide': '/pages/products-bible-guides',
    'Sermon Calendar': '/pages/products-sermon-calendars',
  };

  // Human-readable labels for product types
  static PRODUCT_TYPE_LABELS = {
    'Sermon Series': 'Sermon Series',
    'Illustration': 'Illustrations',
    'Announcement': 'Graphics',
    'Video': 'Videos',
    'Social Calendar': 'Social Media',
    'Docs & Forms': 'Docs & Forms',
    'Bible Guide': 'Bible Guides',
    'Sermon Calendar': 'Sermon Calendars',
  };

  // Product type filters for each search mode
  static SEARCH_MODE_FILTERS = {
    'sermons': 'product_type:"Sermon Series"',
    'illustrations': 'product_type:"Illustration"',
    'graphics': 'product_type:"Announcement"',
    'videos': 'product_type:"Video"',
    'social': 'product_type:"Social Calendar"',
    'docs-forms': 'product_type:"Docs & Forms"',
    'bible-guides': 'product_type:"Bible Guide"',
    'sermon-calendars': 'product_type:"Sermon Calendar"',
    'all_resources': null
  };

  connectedCallback() {
    // Detect search mode from data attribute
    this.searchMode = this.dataset.searchMode || 'all_resources';

    // Initialize from URL
    const urlParams = new URLSearchParams(window.location.search);
    this.activeSearchQuery = urlParams.get('q') || '';

    // Check if this is a single product type mode (sermons, illustrations, graphics, videos)
    const isSingleTypeMode = ['sermons', 'illustrations', 'graphics', 'videos', 'social', 'docs-forms', 'bible-guides', 'sermon-calendars'].includes(this.searchMode);

    if (isSingleTypeMode) {
      // Single type mode - track category and topic values
      this.activeCategoryValue = urlParams.get('seriesType') || '';
      this.activeSubCategoryValue = urlParams.get('subCategory') || '';

      // Topics can be an array in the URL (topics[0], topics[1], etc.) or legacy single 'topic'
      const topicsParam = urlParams.get('topics[0]') || urlParams.get('topic') || '';
      this.activeTopicValue = topicsParam;

      // Determine active filter type and set active tab
      if (this.activeTopicValue) {
        this.activeFilterType = 'topic';
        this.setActiveTabByFilter('topic', this.activeTopicValue);
      } else if (this.activeCategoryValue) {
        this.activeFilterType = 'category';
        this.setActiveTabByFilter('category', this.activeCategoryValue);
      } else {
        this.activeFilterType = null;
        this.setActiveTabByFilter('all', '');
      }
    } else {
      // All resources mode - track product type
      this.activeProductType = urlParams.get('type') || '';
      this.updateHeader();
      this.setActiveTab(this.activeProductType);
    }

    // Wait for InstantSearch libraries to load
    this.waitForLibraries().then(() => {
      this.initializeSearch();
      this.setupFilterToggle();
      this.setupCategoryTabs();

      // Apply filter visibility on page load
      if (this.searchMode === 'social') {
        // Dedicated Social Calendars page
        this.updateSeriesFiltersVisibility('Social Calendar');
      } else if (this.searchMode === 'all_resources' && this.activeProductType) {
        // All Resources page with a product type selected via URL
        this.updateSeriesFiltersVisibility(this.activeProductType);

        // Hide sort for Social Calendar on page load
        if (this.activeProductType === 'Social Calendar') {
          const sortWrapper = document.querySelector('.algolia-search-sort');
          if (sortWrapper) {
            sortWrapper.hidden = true;
          }
        }
      }
    });
  }

  updateHeader(productType = null) {
    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get('q') || '';
    const type = productType !== null ? productType : (urlParams.get('type') || '');

    const titleElement = document.querySelector('.algolia-page-title');
    const descriptionElement = document.querySelector('.algolia-page-description');

    // Get content for this type
    const content = AlgoliaSearchResults.HEADER_CONTENT[type] || AlgoliaSearchResults.HEADER_CONTENT[''];

    // Check if there's an active search query (from URL or stored)
    const activeQuery = this.activeSearchQuery !== undefined ? this.activeSearchQuery : query;

    if (activeQuery) {
      // Has search query - show search results format
      if (titleElement) {
        titleElement.innerHTML = `Search Results for '<span data-search-query>${this.escapeHtml(activeQuery)}</span>'`;
      }
      if (descriptionElement) {
        descriptionElement.innerHTML = `Discover resources inspired by your '<span data-search-query-description>${this.escapeHtml(activeQuery)}</span>' search.`;
      }
    } else {
      // No search query - show category-based header
      if (titleElement) {
        titleElement.textContent = content.title;
      }
      if (descriptionElement) {
        descriptionElement.textContent = content.description;
      }
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  waitForLibraries() {
    return new Promise((resolve) => {
      const check = () => {
        if (window.algoliasearch && window.instantsearch) {
          resolve();
        } else {
          setTimeout(check, 50);
        }
      };
      check();
    });
  }

  initializeSearch() {
    const appId = this.dataset.appId;
    const apiKey = this.dataset.apiKey;
    const indexName = this.dataset.indexName;
    const hitsPerPage = parseInt(this.dataset.hitsPerPage) || 24;

    const searchClient = window.algoliasearch(appId, apiKey);
    this.searchClient = searchClient;
    this.indexName = indexName;

    this.search = window.instantsearch({
      indexName: indexName,
      searchClient: searchClient,
      routing: {
        router: window.instantsearch.routers.history({
          createURL({ qsModule, routeState, location }) {
            const baseUrl = location.origin + location.pathname;
            const queryString = qsModule.stringify(routeState, {
              addQueryPrefix: true,
              arrayFormat: 'indices',
              encode: false,
            });
            return `${baseUrl}${queryString}`;
          },
          parseURL({ qsModule, location }) {
            return qsModule.parse(location.search.slice(1), {
              arrayLimit: 100,
            });
          },
        }),
        stateMapping: {
          stateToRoute(uiState) {
            const indexUiState = uiState[indexName] || {};
            // SubCategory now uses refinementList (array), take first value for URL
            const subCategoryArray = indexUiState.refinementList && indexUiState.refinementList['named_tags.SubCategory'];
            // Topics from refinementList (can be from sidebar or topic tab)
            const topicsArray = indexUiState.refinementList && indexUiState.refinementList['named_tags.Topic'];

            // Strip index name prefix from sortBy for cleaner URLs
            // e.g., 'shopify_products_recent' -> 'recent', 'shopify_products' -> 'relevance'
            let sortValue = indexUiState.sortBy;
            if (sortValue) {
              if (sortValue === indexName) {
                sortValue = 'relevance';
              } else if (sortValue.startsWith(`${indexName}_`)) {
                sortValue = sortValue.replace(`${indexName}_`, '');
              }
            }

            return {
              q: indexUiState.query,
              page: indexUiState.page,
              type: indexUiState.menu && indexUiState.menu['product_type'],
              seriesType: indexUiState.menu && indexUiState.menu['named_tags.Category'],
              subCategory: subCategoryArray && subCategoryArray[0],
              weeks: indexUiState.range && indexUiState.range['meta.custom.weeks_number'],
              topics: topicsArray, // Topics from tabs or sidebar
              verse: indexUiState.refinementList && indexUiState.refinementList['named_tags.Verse'],
              sort: sortValue,
            };
          },
          routeToState(routeState) {
            // Handle both 'topic' (legacy single) and 'topics' (array) URL params
            let topicsValue = routeState.topics;
            if (routeState.topic && !topicsValue) {
              topicsValue = [routeState.topic];
            }

            // Convert short sort value back to full index name
            // e.g., 'recent' -> 'shopify_products_recent', 'relevance' -> 'shopify_products'
            // Default: Recent when browsing (no query), Relevance when searching (query present)
            let sortByValue = routeState.q ? indexName : `${indexName}_recent`;
            if (routeState.sort) {
              if (routeState.sort === 'relevance') {
                sortByValue = indexName;
              } else {
                sortByValue = `${indexName}_${routeState.sort}`;
              }
            }

            return {
              [indexName]: {
                query: routeState.q,
                page: routeState.page,
                menu: {
                  ...(routeState.type && { 'product_type': routeState.type }),
                  ...(routeState.seriesType && { 'named_tags.Category': routeState.seriesType }),
                },
                range: routeState.weeks && {
                  'meta.custom.weeks_number': routeState.weeks,
                },
                refinementList: {
                  ...(topicsValue && { 'named_tags.Topic': topicsValue }),
                  ...(routeState.verse && { 'named_tags.Verse': routeState.verse }),
                  ...(routeState.subCategory && { 'named_tags.SubCategory': [routeState.subCategory] }),
                },
                sortBy: sortByValue,
              },
            };
          },
        },
      },
    });

    this.addWidgets(hitsPerPage);
    this.search.start();

    // Handle no results state and search query refinement tag
    this.search.on('render', () => {
      this.updateNoResultsState();
      this.updateSearchQueryTag();
    });
  }

  addWidgets(hitsPerPage) {
    const { configure, searchBox, hits, menu, refinementList, rangeInput, pagination, stats, clearRefinements, currentRefinements } = window.instantsearch.widgets;
    const { connectMenu } = window.instantsearch.connectors;

    // Get initial query from URL
    const urlParams = new URLSearchParams(window.location.search);
    const initialQuery = urlParams.get('q') || '';

    // Check if this is a single product type mode
    const isSingleTypeMode = ['sermons', 'illustrations', 'graphics', 'videos', 'social', 'docs-forms', 'bible-guides', 'sermon-calendars'].includes(this.searchMode);
    const baseFilter = AlgoliaSearchResults.SEARCH_MODE_FILTERS[this.searchMode];

    this.search.addWidgets([
      configure({
        hitsPerPage: hitsPerPage,
        query: initialQuery,
        // Apply base filter for single type modes
        ...(baseFilter && { filters: baseFilter }),
      }),

      // Sidebar Search Box (only if container exists)
      ...(document.querySelector('#algolia-sidebar-search') ? [searchBox({
        container: '#algolia-sidebar-search',
        placeholder: 'Search...',
        showReset: true,
        showSubmit: false,
        showLoadingIndicator: false,
        cssClasses: {
          root: 'algolia-sidebar-searchbox',
          form: 'algolia-sidebar-searchbox-form',
          input: 'algolia-sidebar-searchbox-input',
          reset: 'algolia-sidebar-searchbox-reset',
          resetIcon: 'algolia-sidebar-searchbox-reset-icon',
        },
        queryHook: (query, search) => {
          // Update the active search query for header updates
          this.activeSearchQuery = query;
          search(query);
        },
      })] : []),

      // Product Type (Virtual menu for category tabs) - only for all_resources mode
      ...(!isSingleTypeMode ? [this.createProductTypeWidget(connectMenu)] : []),

      // Category widget for single type mode tabs (sermons, illustrations, graphics, videos)
      ...(isSingleTypeMode ? [this.createCategoryWidget(connectMenu)] : []),

      // Stats
      stats({
        container: '#algolia-stats',
        templates: {
          text: ({ nbHits }) => {
            return `<span class="algolia-stats-count">${nbHits.toLocaleString()}</span> results`;
          },
        },
      }),

      // Sort by (custom widget to conditionally show series length options)
      this.createSortWidget(),

      // Clear refinements
      clearRefinements({
        container: '#algolia-clear-refinements',
        templates: {
          resetLabel: 'Clear All',
        },
        cssClasses: {
          button: 'algolia-clear-button',
          disabledButton: 'algolia-clear-button--disabled',
        },
      }),

      // Current refinements (tags)
      currentRefinements({
        container: '#algolia-current-refinements',
        cssClasses: {
          root: 'algolia-current-refinements',
          list: 'algolia-current-refinements-list',
          item: 'algolia-current-refinement',
          label: 'algolia-current-refinement-label',
          delete: 'algolia-current-refinement-delete',
        },
        transformItems: (items) => {
          return items.map(item => ({
            ...item,
            refinements: item.refinements.map(refinement => ({
              ...refinement,
              label: this.formatRefinementLabel(refinement.label),
            })),
          }));
        },
      }),

      // Series Type (Radio/Menu with "All" option)
      this.createSeriesTypeWidget({
        container: '#algolia-series-type',
        attribute: 'named_tags.Category',
      }),

      // SubCategory widget (stores subcategory data for use by category widget)
      this.createSubCategoryWidget({
        attribute: 'named_tags.SubCategory',
      }),

      // Series Length (Custom Single Slider for exact value selection)
      this.createSingleSliderWidget({
        container: '#algolia-series-length',
        attribute: 'meta.custom.weeks_number',
      }),

      // Topics (Checkboxes) - using custom widget to capture refine function for topic tabs
      // Hidden for Bible Guides page
      ...(this.searchMode !== 'bible-guides' ? [this.createTopicsWidget({
        container: '#algolia-topics',
        attribute: 'named_tags.Topic',
      })] : []),

      // Bible Passage (Searchable refinement list) - Hidden for all products
      // refinementList({
      //   container: '#algolia-bible-passage',
      //   attribute: 'named_tags.Verse',
      //   operator: 'or',
      //   limit: 8,
      //   showMore: true,
      //   showMoreLimit: 50,
      //   searchable: true,
      //   searchablePlaceholder: 'Type a passage, i.e. John 1',
      //   sortBy: ['isRefined:desc', 'count:desc', 'name:asc'],
      //   cssClasses: {
      //     root: 'algolia-refinement-list algolia-refinement-list--searchable',
      //     searchBox: 'algolia-refinement-search',
      //     list: 'algolia-refinement-list-items',
      //     item: 'algolia-refinement-item',
      //     selectedItem: 'algolia-refinement-item--selected',
      //     label: 'algolia-refinement-label',
      //     checkbox: 'algolia-refinement-checkbox',
      //     labelText: 'algolia-refinement-text',
      //     count: 'algolia-refinement-count',
      //     showMore: 'algolia-show-more',
      //     disabledShowMore: 'algolia-show-more--disabled',
      //   },
      // }),

      // Hits (Product Grid)
      hits({
        container: '#algolia-hits',
        cssClasses: {
          root: 'algolia-hits',
          list: 'algolia-hits-list',
          item: 'algolia-hit',
        },
        templates: {
          item: (hit, { html, components }) => this.renderHit(hit, html, components),
          empty: () => '',
        },
      }),

      // Pagination
      pagination({
        container: '#algolia-pagination',
        padding: 2,
        showFirst: false,
        showLast: false,
        cssClasses: {
          root: 'algolia-pagination',
          list: 'algolia-pagination-list',
          item: 'algolia-pagination-item',
          selectedItem: 'algolia-pagination-item--selected',
          disabledItem: 'algolia-pagination-item--disabled',
          link: 'algolia-pagination-link',
        },
      }),
    ]);
  }

  renderHit(hit, html, components) {
    const image = hit.image || hit.product_image || '';
    const title = hit.title || '';
    const description = this.truncateText(hit.body_html_safe || '', 120);
    const url = `/products/${hit.handle}`;
    // const weeksNumber = hit.meta?.custom?.weeks_number;
    const isBibleGuide = hit.product_type === 'Bible Guide';

    return html`
      <a href="${url}" class="algolia-hit-card ${isBibleGuide ? 'algolia-hit-card--bible-guide' : ''}">
        <div class="algolia-hit-image-wrapper">
          ${image
            ? html`<img
                src="${image}"
                alt="${title}"
                class="algolia-hit-image"
                loading="lazy"
                width="300"
                height="300"
              />`
            : html`<div class="algolia-hit-image-placeholder"></div>`
          }
        </div>
        <div class="algolia-hit-content">
          <h3 class="algolia-hit-title">${components.Highlight({ hit, attribute: 'title' })}</h3>
          ${description
            ? html`<p class="algolia-hit-description">${description}</p>`
            : ''
          }
          ${/* weeksNumber
            ? html`<span class="algolia-hit-weeks-badge">${weeksNumber} ${weeksNumber === 1 ? 'Week Series' : 'Weeks Series'}</span>`
            : ''
          */ ''}
        </div>
      </a>
    `;
  }

  truncateText(text, maxLength) {
    if (!text) return '';
    // Strip HTML tags
    const stripped = text.replace(/<[^>]*>/g, '').trim();
    if (stripped.length <= maxLength) return stripped;
    return stripped.substring(0, maxLength).trim() + '...';
  }

  formatRefinementLabel(label) {
    // Remove prefixes like "Series Type > "
    if (label.includes(' > ')) {
      return label.split(' > ').pop();
    }
    return label;
  }

  /**
   * Merge current Algolia items with all known values.
   * Items not in current results get count=0 and isRefined=false.
   * Preserves the original order from allValues (set on first load).
   */
  mergeWithAllValues(currentItems, allValues) {
    if (!allValues || allValues.length === 0) {
      return currentItems;
    }

    // Create a map of current items for quick lookup
    const currentMap = new Map(
      currentItems.map(item => [item.value, item])
    );

    // Build merged array preserving the original order from allValues
    return allValues.map(knownItem => {
      const currentItem = currentMap.get(knownItem.value);
      if (currentItem) {
        return currentItem;
      } else {
        return {
          value: knownItem.value,
          label: knownItem.label,
          count: 0,
          isRefined: false,
        };
      }
    });
  }

  /**
   * Update category counts and disabled states in place without full re-render.
   * This prevents the "bounce" effect when counts temporarily reset during search.
   */
  updateCategoryCounts(containerEl, categoryItems) {
    categoryItems.forEach(item => {
      const link = containerEl.querySelector(`[data-series-type-value="${item.value}"]`);
      if (!link) return;

      const countEl = link.querySelector('.algolia-menu-count');
      if (countEl) {
        countEl.textContent = item.count;
      }

      const listItem = link.closest('.algolia-menu-item');
      const isDisabled = item.count === 0;

      // Update disabled state
      link.classList.toggle('algolia-menu-link--disabled', isDisabled);
      if (listItem) {
        listItem.classList.toggle('algolia-menu-item--disabled', isDisabled);
      }
      if (isDisabled) {
        link.setAttribute('aria-disabled', 'true');
        link.setAttribute('tabindex', '-1');
      } else {
        link.removeAttribute('aria-disabled');
        link.removeAttribute('tabindex');
      }

      // Update selected state
      if (listItem) {
        listItem.classList.toggle('algolia-menu-item--selected', item.isRefined);
      }
    });

    // Update "All" selected state
    const allLink = containerEl.querySelector('[data-series-type-all]');
    if (allLink) {
      const allListItem = allLink.closest('.algolia-menu-item');
      const hasRefinement = categoryItems.some(item => item.isRefined);
      if (allListItem) {
        allListItem.classList.toggle('algolia-menu-item--selected', !hasRefinement);
      }
    }
  }

  updateNoResultsState() {
    const hitsContainer = document.querySelector('#algolia-hits');
    const noResultsContainer = document.querySelector('#algolia-no-results');

    if (!hitsContainer || !noResultsContainer) return;

    const hasResults = hitsContainer.querySelector('.algolia-hit');
    noResultsContainer.hidden = !!hasResults;

    // In any single-type mode OR when filtering by product type in all_resources mode with an active query and no results, show other product type results
    const isSingleTypeMode = ['sermons', 'illustrations', 'graphics', 'videos', 'social', 'docs-forms', 'bible-guides', 'sermon-calendars'].includes(this.searchMode);
    const isFilteredAllResources = this.searchMode === 'all_resources' && this.activeProductType;

    if (!hasResults && (isSingleTypeMode || isFilteredAllResources) && this.activeSearchQuery) {
      this.fetchAndRenderOtherResults(this.activeSearchQuery);
    } else {
      this.clearOtherResults();
    }
  }

  async fetchAndRenderOtherResults(query) {
    const container = document.querySelector('#algolia-other-results');
    const noResultsContainer = document.querySelector('#algolia-no-results');
    if (!container || !this.searchClient || !this.indexName) return;

    // Show loading state
    container.innerHTML = '<div class="algolia-other-results-loading"><div class="loading-spinner loading-spinner--dark"></div></div>';
    container.hidden = false;
    if (noResultsContainer) noResultsContainer.hidden = true;

    try {
      const results = await this.searchClient.search([{
        indexName: this.indexName,
        query: query,
        params: { hitsPerPage: 40 }
      }]);

      const hits = results.results[0].hits;

      if (!hits || hits.length === 0) {
        container.hidden = true;
        if (noResultsContainer) noResultsContainer.hidden = false;
        return;
      }

      // Group hits by product_type, preserving insertion order
      const groups = {};
      hits.forEach(hit => {
        const type = hit.product_type || 'Other';
        if (!groups[type]) groups[type] = [];
        groups[type].push(hit);
      });

      // Use the active category tab label if set, otherwise fall back to the mode's display name
      const modeLabels = {
        'sermons': 'Sermon Series',
        'illustrations': 'Illustrations',
        'graphics': 'Graphics',
        'videos': 'Videos',
        'social': 'Social Media',
        'docs-forms': 'Docs & Forms',
        'bible-guides': 'Bible Guides',
        'sermon-calendars': 'Sermon Calendars',
      };
      // For all_resources mode with a product type filter, use the product type label
      let sectionLabel = this.activeCategoryValue || modeLabels[this.searchMode];
      if (this.searchMode === 'all_resources' && this.activeProductType) {
        sectionLabel = AlgoliaSearchResults.PRODUCT_TYPE_LABELS[this.activeProductType] || this.activeProductType;
      }
      sectionLabel = sectionLabel || 'this category';
      const ITEMS_PER_GROUP = 8; // 2 rows at 4 columns

      let html = `
        <div class="algolia-other-results-message">
          No results for <strong>"${this.escapeHtml(query)}"</strong> in ${this.escapeHtml(sectionLabel)} — here are some other resources that match:
        </div>
      `;

      Object.entries(groups).forEach(([productType, groupHits]) => {
        const pageUrl = AlgoliaSearchResults.PRODUCT_TYPE_PAGE_URLS[productType];
        const label = AlgoliaSearchResults.PRODUCT_TYPE_LABELS[productType] || productType;
        const visibleHits = groupHits.slice(0, ITEMS_PER_GROUP);
        const seeMoreUrl = pageUrl ? `${pageUrl}?q=${encodeURIComponent(query)}` : null;

        const cardsHtml = visibleHits.map(hit => {
          const image = hit.image || hit.product_image || '';
          const title = this.escapeHtml(hit.title || '');
          const description = this.truncateText(hit.body_html_safe || '', 120);
          const url = `/products/${hit.handle}`;

          return `
            <li class="algolia-hit">
              <a href="${url}" class="algolia-hit-card">
                <div class="algolia-hit-image-wrapper">
                  ${image
                    ? `<img src="${image}" alt="${title}" class="algolia-hit-image" loading="lazy" width="300" height="300">`
                    : '<div class="algolia-hit-image-placeholder"></div>'
                  }
                </div>
                <div class="algolia-hit-content">
                  <h3 class="algolia-hit-title">${title}</h3>
                  ${description ? `<p class="algolia-hit-description">${description}</p>` : ''}
                </div>
              </a>
            </li>
          `;
        }).join('');

        html += `
          <div class="algolia-other-results-group">
            <div class="algolia-other-results-group-header">
              <h3 class="algolia-other-results-group-title">${this.escapeHtml(label)}</h3>
              ${seeMoreUrl ? `
                <a href="${seeMoreUrl}" class="algolia-other-results-see-more">
                  See all results
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                    <path d="M3 7h8M8 3l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </a>
              ` : ''}
            </div>
            <ol class="algolia-hits-list">
              ${cardsHtml}
            </ol>
          </div>
        `;
      });

      container.innerHTML = html;

    } catch (error) {
      console.error('Error fetching other results:', error);
      container.hidden = true;
      if (noResultsContainer) noResultsContainer.hidden = false;
    }
  }

  clearOtherResults() {
    const container = document.querySelector('#algolia-other-results');
    if (container) {
      container.hidden = true;
      container.innerHTML = '';
    }
  }

  updateSearchQueryTag() {
    const container = document.querySelector('#algolia-current-refinements');
    if (!container) return;

    // Remove existing search query tag if present
    const existingTag = container.querySelector('[data-search-query-tag]');
    if (existingTag) {
      existingTag.remove();
    }

    // Add search query tag if there's an active query
    if (this.activeSearchQuery) {
      const tagHtml = `
        <div class="algolia-search-query-tag" data-search-query-tag>
          <span class="algolia-search-query-tag-label">Search:</span>
          <span class="algolia-search-query-tag-value">${this.escapeHtml(this.activeSearchQuery)}</span>
          <button type="button" class="algolia-search-query-tag-remove" aria-label="Clear search">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path d="M9 3L3 9M3 3l6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      `;

      // Insert at the beginning of the container
      container.insertAdjacentHTML('afterbegin', tagHtml);

      // Add click handler to remove button
      const removeBtn = container.querySelector('[data-search-query-tag] .algolia-search-query-tag-remove');
      if (removeBtn) {
        removeBtn.addEventListener('click', () => {
          this.clearSearchQuery();
        });
      }
    }
  }

  clearSearchQuery() {
    // Clear the search query
    this.activeSearchQuery = '';

    // Update the URL to remove the query param but keep other params
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete('q');

    // Build new URL
    const newUrl = urlParams.toString()
      ? `${window.location.pathname}?${urlParams.toString()}`
      : window.location.pathname;

    // Navigate to new URL (this will reload without the search query)
    window.location.href = newUrl;
  }

  setupFilterToggle() {
    const toggleButton = this.querySelector('[data-toggle-filters]');
    const closeButton = this.querySelector('[data-close-filters]');
    const overlay = this.querySelector('[data-filters-overlay]');
    const container = this.querySelector('.algolia-search-container');
    const toggleText = this.querySelector('[data-toggle-filters-text]');

    // Apply initial filter visibility state (hidden on mobile by default)
    this.updateFiltersVisibility(container, toggleButton, toggleText);

    if (toggleButton) {
      toggleButton.addEventListener('click', () => {
        this.filtersVisible = !this.filtersVisible;
        this.updateFiltersVisibility(container, toggleButton, toggleText);
      });
    }

    if (closeButton) {
      closeButton.addEventListener('click', () => {
        // On mobile, close the drawer
        if (window.innerWidth < 990) {
          this.filtersVisible = false;
          this.updateFiltersVisibility(container, toggleButton, toggleText);
        }
      });
    }

    if (overlay) {
      overlay.addEventListener('click', () => {
        this.filtersVisible = false;
        this.updateFiltersVisibility(container, toggleButton, toggleText);
      });
    }

    // Handle all close-filters buttons (including Apply)
    this.querySelectorAll('[data-close-filters]').forEach(btn => {
      btn.addEventListener('click', () => {
        if (window.innerWidth < 990) {
          this.filtersVisible = false;
          this.updateFiltersVisibility(container, toggleButton, toggleText);
        }
      });
    });

    // Close filters when resizing from desktop to mobile while filters are open
    let wasDesktop = window.innerWidth >= 990;
    window.addEventListener('resize', () => {
      const isDesktop = window.innerWidth >= 990;
      // Only act when crossing the breakpoint from desktop to mobile
      if (wasDesktop && !isDesktop && this.filtersVisible) {
        this.filtersVisible = false;
        this.updateFiltersVisibility(container, toggleButton, toggleText);
      }
      wasDesktop = isDesktop;
    });
  }

  updateFiltersVisibility(container, toggleButton, toggleText) {
    if (container) {
      container.dataset.filtersVisible = this.filtersVisible;
    }
    if (toggleButton) {
      toggleButton.setAttribute('aria-expanded', this.filtersVisible);
    }
    if (toggleText) {
      toggleText.textContent = this.filtersVisible ? 'Hide Filters' : 'Show Filters';
    }

    // Update toolbar grid to match filter visibility
    const toolbar = this.querySelector('[data-search-toolbar]');
    if (toolbar) {
      if (this.filtersVisible) {
        toolbar.removeAttribute('data-filters-hidden');
      } else {
        toolbar.setAttribute('data-filters-hidden', '');
      }
    }

    // Toggle body scroll on mobile when drawer is open
    if (window.innerWidth < 990) {
      document.body.style.overflow = this.filtersVisible ? 'hidden' : '';
    }
  }

  clearSubCategoryIfNeeded() {
    // Clear subcategory refinement if one is active
    if (this.activeSubCategoryValue && this.subCategoryRefine) {
      const refinedSubCat = this.subCategoryItems.find(item => item.isRefined);
      if (refinedSubCat) {
        this.subCategoryRefine(refinedSubCat.value);
      }
      this.activeSubCategoryValue = '';
    }
  }

  createSeriesTypeWidget({ container, attribute }) {
    const { connectMenu } = window.instantsearch.connectors;

    const renderSeriesTypeMenu = (renderOptions, isFirstRender) => {
      const { items, refine, canRefine } = renderOptions;
      const containerEl = document.querySelector(container);

      if (!containerEl) return;

      // Store category refine function for external use
      this.categoryRefine = refine;

      // Store last render options so subcategory widget can trigger re-render
      this.lastCategoryRenderOptions = renderOptions;

      // Clean up labels by trimming leading/trailing whitespace
      const cleanedItems = items.map(item => ({
        ...item,
        label: item.label.trim(),
      }));

      // Cache all category values with their initial counts (only once on first load)
      if (this.allCategoryValues === null && cleanedItems.length > 0) {
        this.allCategoryValues = cleanedItems.map(item => ({
          value: item.value,
          label: item.label,
          initialCount: item.count, // Store initial count permanently
        }));
      }

      // Merge current items with all known categories, but use initial counts for display
      const categoryItems = this.mergeWithAllValues(cleanedItems, this.allCategoryValues);

      // Skip rendering if no items yet
      if (!categoryItems || categoryItems.length === 0) {
        return;
      }

      // Check if any topics are currently refined
      const hasTopicRefinement = this.topicItems && this.topicItems.some(item => item.isRefined);

      // Use live counts when topics are refined, otherwise use initial counts
      if (hasTopicRefinement) {
        // Topics are selected - show live counts that reflect topic filtering
        categoryItems.forEach(item => {
          item.displayCount = item.count;
        });
      } else if (this.allCategoryValues) {
        // No topics selected - use initial counts (fixed)
        const initialCountMap = new Map(this.allCategoryValues.map(v => [v.value, v.initialCount]));
        categoryItems.forEach(item => {
          const initialCount = initialCountMap.get(item.value);
          if (initialCount !== undefined) {
            item.displayCount = initialCount;
          } else {
            item.displayCount = item.count;
          }
        });
      }

      // Check if any item is currently refined
      const hasRefinement = categoryItems.some(item => item.isRefined);

      // Get subcategories for the selected category (preserving displayCount)
      const selectedCategory = this.activeCategoryValue || '';
      const trimmedCategory = selectedCategory.trim();
      const filteredSubCategories = (this.subCategoryItems || [])
        .filter(item => {
          if (!selectedCategory) return false;
          const trimmedLabel = item.label.trim();
          return trimmedLabel.startsWith(trimmedCategory + ' >');
        });

      // Create a signature that includes topic refinement state (so we re-render when topics change)
      const topicSignature = hasTopicRefinement ? this.topicItems.filter(i => i.isRefined).map(i => i.value).join(',') : 'none';
      const subCatSignature = filteredSubCategories.map(i => `${i.value}:${i.isRefined}`).join('|');
      const currentSignature = categoryItems.map(i => `${i.value}:${i.isRefined}:${i.count}`).join('|') + '||' + subCatSignature + '||topics:' + topicSignature;
      if (this.lastCategorySignature === currentSignature) {
        return; // Skip re-render if selection state unchanged
      }
      this.lastCategorySignature = currentSignature;

      // Build HTML with "All" option first
      let html = `
        <div class="algolia-menu">
          <ul class="algolia-menu-list">
            <li class="algolia-menu-item ${!hasRefinement ? 'algolia-menu-item--selected' : ''}">
              <a href="#" class="algolia-menu-link" data-series-type-all>
                <span class="algolia-menu-label">All</span>
              </a>
            </li>
      `;

      categoryItems.forEach(item => {
        const isSelected = item.isRefined;
        const count = item.displayCount !== undefined ? item.displayCount : item.count;
        const isDisabled = count === 0;
        html += `
          <li class="algolia-menu-item ${isSelected ? 'algolia-menu-item--selected' : ''} ${isDisabled ? 'algolia-menu-item--disabled' : ''}">
            <a href="#" class="algolia-menu-link ${isDisabled ? 'algolia-menu-link--disabled' : ''}" data-series-type-value="${item.value}"${isDisabled ? ' aria-disabled="true" tabindex="-1"' : ''}>
              <span class="algolia-menu-label">${item.label}</span>
              <span class="algolia-menu-count">${count}</span>
            </a>
        `;

        // If this category is selected and has subcategories, render them nested
        if (isSelected && filteredSubCategories.length > 0) {
          const hasSubRefinement = filteredSubCategories.some(sub => sub.isRefined);
          html += `
            <ul class="algolia-subcategory-list">
              <li class="algolia-subcategory-item ${!hasSubRefinement ? 'algolia-subcategory-item--selected' : ''}">
                <a href="#" class="algolia-subcategory-link" data-subcategory-all>
                  <span class="algolia-menu-label">All ${item.label}</span>
                </a>
              </li>
          `;

          filteredSubCategories.forEach(subItem => {
            const subCategoryName = subItem.label.includes(' > ')
              ? subItem.label.split(' > ').slice(1).join(' > ').trim()
              : subItem.label;
            const subCount = subItem.displayCount !== undefined ? subItem.displayCount : subItem.count;
            const isSubDisabled = subCount === 0;

            html += `
              <li class="algolia-subcategory-item ${subItem.isRefined ? 'algolia-subcategory-item--selected' : ''} ${isSubDisabled ? 'algolia-subcategory-item--disabled' : ''}">
                <a href="#" class="algolia-subcategory-link ${isSubDisabled ? 'algolia-subcategory-link--disabled' : ''}" data-subcategory-value="${subItem.value}"${isSubDisabled ? ' aria-disabled="true" tabindex="-1"' : ''}>
                  <span class="algolia-menu-label">${subCategoryName}</span>
                  <span class="algolia-menu-count">${subCount}</span>
                </a>
              </li>
            `;
          });

          html += `</ul>`;
        }

        html += `</li>`;
      });

      html += `</ul></div>`;
      containerEl.innerHTML = html;

      // Add click handlers for categories
      containerEl.querySelectorAll('[data-series-type-value]').forEach(link => {
        link.addEventListener('click', (e) => {
          e.preventDefault();

          // Prevent clicking on disabled items (zero results)
          if (link.classList.contains('algolia-menu-link--disabled')) {
            return;
          }

          const newCategoryValue = link.dataset.seriesTypeValue;

          // Clear subcategory when changing category
          this.clearSubCategoryIfNeeded();

          // Update active category value
          this.activeCategoryValue = newCategoryValue;
          this.setActiveTabByCategory(newCategoryValue);

          refine(newCategoryValue);
        });
      });

      // Add click handlers for subcategories (radio behavior - single selection)
      containerEl.querySelectorAll('[data-subcategory-value]').forEach(link => {
        link.addEventListener('click', (e) => {
          e.preventDefault();

          // Prevent clicking on disabled items (zero results)
          if (link.classList.contains('algolia-subcategory-link--disabled')) {
            return;
          }

          const value = link.dataset.subcategoryValue;

          // Clear any existing subcategory selection first (radio behavior)
          const currentRefined = this.subCategoryItems.find(item => item.isRefined);
          if (currentRefined && currentRefined.value !== value) {
            this.subCategoryRefine(currentRefined.value);
          }

          this.activeSubCategoryValue = value;
          this.subCategoryRefine(value);
        });
      });

      // Add click handler for "All [Category]" subcategory option
      containerEl.querySelectorAll('[data-subcategory-all]').forEach(link => {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          this.activeSubCategoryValue = '';
          // Clear any refined subcategory
          const refinedItem = this.subCategoryItems.find(item => item.isRefined);
          if (refinedItem) {
            this.subCategoryRefine(refinedItem.value);
          }
        });
      });

      // "All" option clears the refinement
      const allLink = containerEl.querySelector('[data-series-type-all]');
      if (allLink) {
        allLink.addEventListener('click', (e) => {
          e.preventDefault();

          // Clear subcategory when clearing category
          this.clearSubCategoryIfNeeded();

          // Update active category value
          this.activeCategoryValue = '';
          this.setActiveTabByCategory('');

          // Find the currently refined item and toggle it off
          const refinedItem = categoryItems.find(item => item.isRefined);
          if (refinedItem) {
            refine(refinedItem.value);
          }
        });
      }
    };

    // Store re-render function so subcategory widget can trigger updates
    this.rerenderCategoryWidget = () => {
      if (this.lastCategoryRenderOptions) {
        renderSeriesTypeMenu(this.lastCategoryRenderOptions, false);
      }
    };

    const customSeriesTypeMenu = connectMenu(renderSeriesTypeMenu);

    return customSeriesTypeMenu({
      attribute: attribute,
      limit: 20,
      sortBy: ['count:desc', 'name:asc'],
    });
  }

  createSubCategoryWidget({ attribute }) {
    // Use connectRefinementList to get disjunctive faceting
    // This widget doesn't render - it just stores items/refine for the Category widget to use
    const { connectRefinementList } = window.instantsearch.connectors;

    const renderSubCategoryMenu = (renderOptions, isFirstRender) => {
      const { items, refine } = renderOptions;

      // Clean up labels
      const cleanedItems = items.map(item => ({
        ...item,
        label: item.label.trim(),
      }));

      // Cache all subcategory values with initial counts (only once on first load)
      if (this.allSubCategoryValues === null && cleanedItems.length > 0) {
        this.allSubCategoryValues = cleanedItems.map(item => ({
          value: item.value,
          label: item.label,
          initialCount: item.count, // Store initial count permanently
        }));
      }

      // Store refine function and merged items for use by category widget
      this.subCategoryRefine = refine;
      this.subCategoryItems = this.mergeWithAllValues(cleanedItems, this.allSubCategoryValues);

      // Check if any topics are currently refined
      const hasTopicRefinement = this.topicItems && this.topicItems.some(item => item.isRefined);

      // Use live counts when topics are refined, otherwise use initial counts
      if (hasTopicRefinement) {
        // Topics are selected - show live counts that reflect topic filtering
        this.subCategoryItems.forEach(item => {
          item.displayCount = item.count;
        });
      } else if (this.allSubCategoryValues) {
        // No topics selected - use initial counts (fixed)
        const initialCountMap = new Map(this.allSubCategoryValues.map(v => [v.value, v.initialCount]));
        this.subCategoryItems.forEach(item => {
          const initialCount = initialCountMap.get(item.value);
          if (initialCount !== undefined) {
            item.displayCount = initialCount;
          } else {
            item.displayCount = item.count;
          }
        });
      }

      // Re-render the category widget to update subcategories display
      // This ensures subcategories are shown after the subcategory data is loaded
      if (this.rerenderCategoryWidget) {
        this.rerenderCategoryWidget();
      }
    };

    const customSubCategoryWidget = connectRefinementList(renderSubCategoryMenu);

    return customSubCategoryWidget({
      attribute: attribute,
      limit: 50,
      operator: 'or', // Disjunctive faceting
    });
  }

  createProductTypeWidget(connectMenu) {
    // Store refine function for use by category tabs
    const renderProductTypeMenu = (renderOptions, isFirstRender) => {
      const { refine, items } = renderOptions;
      this.productTypeRefine = refine;
      this.productTypeItems = items;

      // Note: We don't update tabs here - they're managed via click handlers and URL init
    };

    const customProductTypeMenu = connectMenu(renderProductTypeMenu);

    return customProductTypeMenu({
      attribute: 'product_type',
    });
  }

  createCategoryWidget(connectMenu) {
    // Store refine function for use by sermon mode category tabs
    const renderCategoryMenu = (renderOptions) => {
      const { refine, items } = renderOptions;
      this.categoryRefine = refine;
      this.categoryItems = items;

      // Note: We don't update tabs here - they're managed via click handlers and URL init
    };

    const customCategoryMenu = connectMenu(renderCategoryMenu);

    return customCategoryMenu({
      attribute: 'named_tags.Category',
    });
  }

  createTopicsWidget({ container, attribute }) {
    // Custom topics widget that stores refine function for topic tabs
    // while still rendering the standard refinementList UI
    const { connectRefinementList } = window.instantsearch.connectors;

    const renderTopicsWidget = (renderOptions, isFirstRender) => {
      const { items, refine, canToggleShowMore, isShowingMore, toggleShowMore } = renderOptions;
      const containerEl = document.querySelector(container);

      if (!containerEl) return;

      // Store refine function and items for topic tabs
      this.topicRefine = refine;

      // Cache all topic values only once on first load (preserves initial sort order)
      if (this.allTopicValues === null && items.length > 0) {
        this.allTopicValues = items.map(item => ({
          value: item.value,
          label: item.label,
        }));
      }

      // Use live items directly (no merge - topics are dynamic and hide 0-count items)
      this.topicItems = items;

      // Render the refinement list UI (topics always re-render with live counts)
      const visibleItems = items.slice(0, isShowingMore ? 50 : 10);

      let html = `<div class="algolia-refinement-list">
        <ul class="algolia-refinement-list-items">`;

      visibleItems.forEach(item => {
        const selectedClass = item.isRefined ? 'algolia-refinement-item--selected' : '';
        html += `
          <li class="algolia-refinement-item ${selectedClass}">
            <label class="algolia-refinement-label">
              <input type="checkbox" class="algolia-refinement-checkbox" data-topic-value="${this.escapeHtml(item.value)}" ${item.isRefined ? 'checked' : ''}>
              <span class="algolia-refinement-text">${this.escapeHtml(item.label)}</span>
              <span class="algolia-refinement-count">${item.count}</span>
            </label>
          </li>`;
      });

      html += `</ul>`;

      // Show more button (only if there are more items to show)
      const hasMoreItems = items.length > (isShowingMore ? 50 : 10);
      if (hasMoreItems) {
        html += `<button type="button" class="algolia-show-more">${isShowingMore ? 'Show less' : 'Show more'}</button>`;
      }

      html += `</div>`;
      containerEl.innerHTML = html;

      // Add click handlers for checkboxes
      containerEl.querySelectorAll('[data-topic-value]').forEach(checkbox => {
        checkbox.addEventListener('change', () => {
          refine(checkbox.dataset.topicValue);
        });
      });

      // Add click handler for show more
      const showMoreBtn = containerEl.querySelector('.algolia-show-more');
      if (showMoreBtn) {
        showMoreBtn.addEventListener('click', () => {
          toggleShowMore();
        });
      }
    };

    const customTopicsWidget = connectRefinementList(renderTopicsWidget);

    return customTopicsWidget({
      attribute: attribute,
      operator: 'and',
      limit: 10,
      showMore: true,
      showMoreLimit: 50,
      sortBy: ['isRefined:desc', 'count:desc', 'name:asc'],
    });
  }

  setupCategoryTabs() {
    const tabs = document.querySelectorAll('[data-category-tab]');
    const isSingleTypeMode = ['sermons', 'illustrations', 'graphics', 'videos', 'social', 'docs-forms', 'bible-guides', 'sermon-calendars'].includes(this.searchMode);

    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const filterType = tab.dataset.filterType;
        const filterValue = tab.dataset.filterValue || tab.dataset.productType || '';

        if (isSingleTypeMode) {
          // Handle the different filter types
          if (filterType === 'all') {
            // Clear all filters
            this.clearAllTabFilters();
            this.activeFilterType = null;
            this.setActiveTabByFilter('all', '');
          } else if (filterType === 'category') {
            // Clear topic filter if active, then apply category
            this.clearTopicFilter();
            this.activeCategoryValue = filterValue;
            this.activeFilterType = 'category';
            this.setActiveTabByFilter('category', filterValue);

            // Clear subcategory when changing category
            this.clearSubCategoryIfNeeded();

            if (this.categoryRefine) {
              this.categoryRefine(filterValue || undefined);
            }
          } else if (filterType === 'topic') {
            // Clear category filter if active
            this.clearCategoryFilter();
            // Clear any existing topic refinements first
            this.clearTopicFilter();

            this.activeTopicValue = filterValue;
            this.activeFilterType = 'topic';
            this.setActiveTabByFilter('topic', filterValue);

            // Apply the new topic filter
            if (this.topicRefine && filterValue) {
              this.topicRefine(filterValue);
            }
          }
        } else {
          // All resources mode - filter by product_type
          const productType = tab.dataset.productType;
          this.activeProductType = productType;
          this.setActiveTab(productType);

          if (this.productTypeRefine) {
            // Refine by the product type (empty string clears the filter)
            this.productTypeRefine(productType || undefined);
          }

          // Hide/show sort for Social Calendar - use setTimeout to run after InstantSearch renders
          setTimeout(() => {
            const sortWrapper = document.querySelector('.algolia-search-sort');
            if (sortWrapper) {
              sortWrapper.hidden = productType === 'Social Calendar';
            }
          }, 0);
        }
      });
    });
  }

  setActiveTab(productType) {
    const tabs = document.querySelectorAll('[data-category-tab]');

    tabs.forEach(tab => {
      const tabProductType = tab.dataset.productType;
      const isActive = tabProductType === productType;

      tab.classList.toggle('algolia-category-tab--active', isActive);
      tab.setAttribute('aria-selected', isActive);
    });

    // Show/hide Series Type and Series Length filters based on product type
    this.updateSeriesFiltersVisibility(productType);

    // Update header text based on product type (if no search query)
    this.updateHeader(productType);
  }

  setActiveTabByCategory(categoryValue) {
    const tabs = document.querySelectorAll('[data-category-tab]');

    tabs.forEach(tab => {
      const tabCategoryValue = tab.dataset.categoryValue;
      const isActive = tabCategoryValue === categoryValue;

      tab.classList.toggle('algolia-category-tab--active', isActive);
      tab.setAttribute('aria-selected', isActive);
    });

    // Header title should remain static - do not update based on category tab selection
  }

  setActiveTabByFilter(filterType, filterValue) {
    const tabs = document.querySelectorAll('[data-category-tab]');

    tabs.forEach(tab => {
      const tabFilterType = tab.dataset.filterType;
      const tabFilterValue = tab.dataset.filterValue || '';
      let isActive = false;

      if (filterType === 'all') {
        // "All" tab is active when filterType is 'all'
        isActive = tabFilterType === 'all';
      } else {
        // Match both type and value
        isActive = tabFilterType === filterType && tabFilterValue === filterValue;
      }

      tab.classList.toggle('algolia-category-tab--active', isActive);
      tab.setAttribute('aria-selected', isActive);
    });
  }

  clearCategoryFilter() {
    if (this.activeCategoryValue && this.categoryRefine) {
      // Find and clear the refined category
      const refinedItem = this.categoryItems?.find(item => item.isRefined);
      if (refinedItem) {
        this.categoryRefine(refinedItem.value);
      }
    }
    this.activeCategoryValue = '';
    // Also clear subcategory
    this.clearSubCategoryIfNeeded();
  }

  clearTopicFilter() {
    // Clear all refined topics (refinementList can have multiple)
    if (this.topicRefine && this.topicItems) {
      const refinedItems = this.topicItems.filter(item => item.isRefined);
      refinedItems.forEach(item => {
        this.topicRefine(item.value);
      });
    }
    this.activeTopicValue = '';
  }

  clearAllTabFilters() {
    this.clearCategoryFilter();
    this.clearTopicFilter();
  }

  updateHeaderForSingleTypeMode(categoryValue = '') {
    // Don't override if there's a search query
    if (this.activeSearchQuery) return;

    // Get the appropriate header content based on search mode
    let headerContent;
    switch (this.searchMode) {
      case 'sermons':
        headerContent = AlgoliaSearchResults.SERMON_HEADER_CONTENT;
        break;
      case 'illustrations':
        headerContent = AlgoliaSearchResults.ILLUSTRATIONS_HEADER_CONTENT;
        break;
      case 'graphics':
        headerContent = AlgoliaSearchResults.GRAPHICS_HEADER_CONTENT;
        break;
      case 'videos':
        headerContent = AlgoliaSearchResults.VIDEOS_HEADER_CONTENT;
        break;
      case 'social':
        headerContent = AlgoliaSearchResults.SOCIAL_HEADER_CONTENT;
        break;
      case 'docs-forms':
        headerContent = AlgoliaSearchResults.DOCS_FORMS_HEADER_CONTENT;
        break;
      case 'bible-guides':
        headerContent = AlgoliaSearchResults.BIBLE_GUIDES_HEADER_CONTENT;
        break;
      case 'sermon-calendars':
        headerContent = AlgoliaSearchResults.SERMON_CALENDARS_HEADER_CONTENT;
        break;
      default:
        headerContent = AlgoliaSearchResults.SERMON_HEADER_CONTENT;
    }

    const content = headerContent[categoryValue] || headerContent[''];
    const titleElement = document.querySelector('.algolia-page-title');
    const descriptionElement = document.querySelector('.algolia-page-description');

    if (titleElement) {
      titleElement.textContent = content.title;
    }
    if (descriptionElement) {
      descriptionElement.textContent = content.description;
    }
  }

  updateSeriesFiltersVisibility(productType) {
    // Store current product type for use by renderSortSelect
    this.currentVisibilityProductType = productType;

    // Hide entire filter column for Social Calendars (no filters applicable)
    const hideAllFilters = productType === 'Social Calendar';
    const filtersSidebar = this.querySelector('[data-filters-sidebar]');
    const filtersToggle = this.querySelector('.algolia-toolbar-filters');
    const searchContainer = this.querySelector('.algolia-search-container');
    const sortContainer = this.querySelector('#algolia-sort');

    if (filtersSidebar) {
      filtersSidebar.hidden = hideAllFilters;
    }
    if (filtersToggle) {
      filtersToggle.hidden = hideAllFilters;
    }
    // Hide/show sort container directly
    if (sortContainer) {
      sortContainer.hidden = hideAllFilters;
    }
    // Set filters-visible to false for full-width results when hiding all filters
    if (searchContainer && hideAllFilters) {
      searchContainer.dataset.filtersVisible = 'false';
    }

    // If hiding all filters, skip individual filter visibility updates
    if (hideAllFilters) {
      return;
    }

    // Restore filters-visible state when not hiding all filters
    if (searchContainer && !hideAllFilters) {
      searchContainer.dataset.filtersVisible = this.filtersVisible ? 'true' : 'false';
    }

    // Show series length filter only when "Sermon Series" is selected
    const showSeriesLengthFilter = productType === 'Sermon Series';

    // Hide topics filter for Bible Guides and Docs & Forms
    const showTopicsFilter = productType !== 'Bible Guide' && productType !== 'Docs & Forms';

    // Find the filter group containers
    const seriesLengthContainer = document.querySelector('#algolia-series-length')?.closest('.algolia-filter-group');
    const topicsContainer = document.querySelector('#algolia-topics')?.closest('.algolia-filter-group');

    // Category filter is always visible (no longer hidden based on product type)

    if (seriesLengthContainer) {
      seriesLengthContainer.hidden = !showSeriesLengthFilter;
    }

    if (topicsContainer) {
      topicsContainer.hidden = !showTopicsFilter;
    }

    // Re-render sort widget to show/hide series-related sort options
    this.refreshSortWidget();
  }

  createSortWidget() {
    const { connectSortBy } = window.instantsearch.connectors;
    const indexName = this.dataset.indexName;

    // Base sort options (always shown)
    const baseSortItems = [
      { label: 'Recent', value: `${indexName}_recent` },
      { label: 'Relevance', value: indexName },
    ];

    // Series-specific sort options (only for sermon series)
    const seriesSortItems = [
      { label: 'Shortest Series', value: `${indexName}_weeks_asc` },
      { label: 'Longest Series', value: `${indexName}_weeks_desc` },
    ];

    const renderSortBy = (renderOptions) => {
      const { currentRefinement, options, refine } = renderOptions;
      const container = document.querySelector('#algolia-sort');

      if (!container) return;

      // Store refine function and options for external re-render
      this.sortRefine = refine;
      this.sortOptions = options;
      this.sortCurrentRefinement = currentRefinement;

      this.renderSortSelect(container, options, currentRefinement, refine, indexName);
    };

    const customSortBy = connectSortBy(renderSortBy);

    return customSortBy({
      items: [...baseSortItems, ...seriesSortItems],
    });
  }

  renderSortSelect(container, options, currentRefinement, refine, indexName) {
    // Hide sort entirely for Social Calendars
    // Check both activeProductType and currentVisibilityProductType for consistency
    const hideSortForSocialCalendar = this.searchMode === 'social' ||
      this.activeProductType === 'Social Calendar' ||
      this.currentVisibilityProductType === 'Social Calendar';
    if (hideSortForSocialCalendar) {
      container.hidden = true;
      return;
    }
    container.hidden = false;

    // Determine if series sort options should be shown
    const showSeriesSort = this.shouldShowSeriesSortOptions();

    // Filter options based on whether series sort should be shown
    const filteredOptions = showSeriesSort
      ? options
      : options.filter(opt => !opt.label.includes('Series'));

    // If current refinement is a series sort and we're hiding them, reset to default
    const isSeriesSort = currentRefinement.includes('_weeks_');

    // If we need to reset the sort, do it
    if (!showSeriesSort && isSeriesSort) {
      refine(indexName);
      return; // Will re-render with new refinement
    }

    const effectiveRefinement = currentRefinement;

    container.innerHTML = `
      <select class="algolia-sort-select">
        ${filteredOptions.map(opt => `
          <option value="${opt.value}" ${opt.value === effectiveRefinement ? 'selected' : ''}>
            ${opt.label}
          </option>
        `).join('')}
      </select>
    `;

    const select = container.querySelector('select');
    select.addEventListener('change', (e) => {
      refine(e.target.value);
    });
  }

  refreshSortWidget() {
    // Re-render sort widget with current state when product type changes
    const container = document.querySelector('#algolia-sort');
    if (container && this.sortRefine && this.sortOptions) {
      this.renderSortSelect(
        container,
        this.sortOptions,
        this.sortCurrentRefinement,
        this.sortRefine,
        this.dataset.indexName
      );
    }
  }

  shouldShowSeriesSortOptions() {
    // In sermons mode, always show series sort options
    if (this.searchMode === 'sermons') {
      return true;
    }

    // In all_resources mode, show if no product type selected OR if Sermon Series is selected
    if (this.searchMode === 'all_resources') {
      return this.activeProductType === '' || this.activeProductType === 'Sermon Series';
    }

    // For other single-type modes (illustrations, graphics, videos, etc.), hide series sort
    return false;
  }

  /* COMMENTED OUT: Original dual slider implementation (min/max range)
  createRangeSliderWidget({ container, attribute }) {
    const { connectRange } = window.instantsearch.connectors;

    const renderRangeSlider = (renderOptions, isFirstRender) => {
      const { start, range, refine } = renderOptions;
      const containerEl = document.querySelector(container);

      if (!containerEl) return;

      const min = range.min || 1;
      const max = range.max || 52;

      // Check if filters are actively applied
      const hasMinFilter = start[0] !== -Infinity && start[0] !== undefined;
      const hasMaxFilter = start[1] !== Infinity && start[1] !== undefined;
      const currentMin = hasMinFilter ? start[0] : min;
      const currentMax = hasMaxFilter ? start[1] : max;

      if (isFirstRender) {
        containerEl.innerHTML = `
          <div class="algolia-range-slider algolia-range-slider--dual">
            <div class="algolia-range-slider-track" data-slider-track>
              <div class="algolia-range-slider-fill" data-slider-fill></div>
              <div class="algolia-range-slider-thumb algolia-range-slider-thumb--min" data-slider-thumb-min></div>
              <div class="algolia-range-slider-thumb algolia-range-slider-thumb--max" data-slider-thumb-max></div>
            </div>
            <div class="algolia-range-slider-labels">
              <input type="number" class="algolia-range-slider-value" data-slider-input-min min="${min}" max="${max}" value="${currentMin}">
              <span class="algolia-range-slider-separator">to</span>
              <input type="number" class="algolia-range-slider-value" data-slider-input-max min="${min}" max="${max}" value="${currentMax}">
              <span class="algolia-range-slider-unit">Weeks</span>
            </div>
          </div>
        `;

        const track = containerEl.querySelector('[data-slider-track]');
        const thumbMin = containerEl.querySelector('[data-slider-thumb-min]');
        const thumbMax = containerEl.querySelector('[data-slider-thumb-max]');
        const fill = containerEl.querySelector('[data-slider-fill]');
        const inputMin = containerEl.querySelector('[data-slider-input-min]');
        const inputMax = containerEl.querySelector('[data-slider-input-max]');

        let draggingThumb = null;
        let currentValues = { min: currentMin, max: currentMax };

        const updateSlider = (minVal, maxVal) => {
          currentValues = { min: minVal, max: maxVal };
          const minPercentage = ((minVal - min) / (max - min)) * 100;
          const maxPercentage = ((maxVal - min) / (max - min)) * 100;

          thumbMin.style.left = `${minPercentage}%`;
          thumbMax.style.left = `${maxPercentage}%`;
          fill.style.left = `${minPercentage}%`;
          fill.style.width = `${maxPercentage - minPercentage}%`;

          inputMin.value = minVal;
          inputMax.value = maxVal;
        };

        // Initialize slider position
        updateSlider(currentMin, currentMax);

        const getValueFromPosition = (clientX) => {
          const rect = track.getBoundingClientRect();
          const percentage = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
          return Math.round(min + percentage * (max - min));
        };

        const handleMove = (clientX) => {
          if (!draggingThumb) return;
          let value = getValueFromPosition(clientX);

          if (draggingThumb === 'min') {
            value = Math.min(value, currentValues.max - 1);
            value = Math.max(value, min);
            updateSlider(value, currentValues.max);
          } else {
            value = Math.max(value, currentValues.min + 1);
            value = Math.min(value, max);
            updateSlider(currentValues.min, value);
          }
        };

        const handleEnd = () => {
          if (!draggingThumb) return;
          draggingThumb = null;
          refine([currentValues.min, currentValues.max]);
          document.removeEventListener('mousemove', onMouseMove);
          document.removeEventListener('mouseup', onMouseUp);
          document.removeEventListener('touchmove', onTouchMove);
          document.removeEventListener('touchend', onTouchEnd);
        };

        const onMouseMove = (e) => handleMove(e.clientX);
        const onMouseUp = () => handleEnd();
        const onTouchMove = (e) => handleMove(e.touches[0].clientX);
        const onTouchEnd = () => handleEnd();

        const startDrag = (thumb) => {
          draggingThumb = thumb;
          document.addEventListener('mousemove', onMouseMove);
          document.addEventListener('mouseup', onMouseUp);
        };

        const startDragTouch = (thumb) => {
          draggingThumb = thumb;
          document.addEventListener('touchmove', onTouchMove);
          document.addEventListener('touchend', onTouchEnd);
        };

        thumbMin.addEventListener('mousedown', (e) => {
          e.preventDefault();
          startDrag('min');
        });

        thumbMax.addEventListener('mousedown', (e) => {
          e.preventDefault();
          startDrag('max');
        });

        thumbMin.addEventListener('touchstart', () => {
          startDragTouch('min');
        }, { passive: true });

        thumbMax.addEventListener('touchstart', () => {
          startDragTouch('max');
        }, { passive: true });

        track.addEventListener('click', (e) => {
          if (e.target === thumbMin || e.target === thumbMax) return;

          const value = getValueFromPosition(e.clientX);
          const distToMin = Math.abs(value - currentValues.min);
          const distToMax = Math.abs(value - currentValues.max);

          if (distToMin <= distToMax) {
            const newMin = Math.min(value, currentValues.max - 1);
            updateSlider(newMin, currentValues.max);
          } else {
            const newMax = Math.max(value, currentValues.min + 1);
            updateSlider(currentValues.min, newMax);
          }
          refine([currentValues.min, currentValues.max]);
        });

        inputMin.addEventListener('change', (e) => {
          let value = parseInt(e.target.value, 10);
          value = Math.max(min, Math.min(currentValues.max - 1, value));
          updateSlider(value, currentValues.max);
          refine([currentValues.min, currentValues.max]);
        });

        inputMax.addEventListener('change', (e) => {
          let value = parseInt(e.target.value, 10);
          value = Math.max(currentValues.min + 1, Math.min(max, value));
          updateSlider(currentValues.min, value);
          refine([currentValues.min, currentValues.max]);
        });

        // Store update function for later renders
        containerEl._updateSlider = updateSlider;
      }

      // Update slider position on subsequent renders
      if (containerEl._updateSlider) {
        containerEl._updateSlider(currentMin, currentMax);
      }
    };

    const customRangeSlider = connectRange(renderRangeSlider);

    return customRangeSlider({
      attribute: attribute,
    });
  }
  END COMMENTED OUT dual slider */

  // Single slider widget for exact value selection
  createSingleSliderWidget({ container, attribute }) {
    const { connectRange } = window.instantsearch.connectors;

    const renderSingleSlider = (renderOptions, isFirstRender) => {
      const { start, range, refine } = renderOptions;
      const containerEl = document.querySelector(container);

      if (!containerEl) return;

      const min = range.min || 1;
      const max = range.max || 52;

      // Check if a filter is actively applied
      const hasFilter = start[0] !== -Infinity && start[0] !== undefined;
      const currentValue = hasFilter ? start[0] : null; // null means "Any"

      if (isFirstRender) {
        containerEl.innerHTML = `
          <div class="algolia-range-slider algolia-range-slider--single" data-has-filter="${hasFilter}">
            <div class="algolia-range-slider-track" data-slider-track>
              <div class="algolia-range-slider-fill" data-slider-fill></div>
              <div class="algolia-range-slider-thumb" data-slider-thumb></div>
            </div>
            <div class="algolia-range-slider-labels algolia-range-slider-labels--single">
              <span class="algolia-range-slider-any" data-slider-any>Any</span>
              <input type="number" class="algolia-range-slider-value" data-slider-input min="${min}" max="${max}" value="${currentValue || min}">
              <span class="algolia-range-slider-unit">Weeks</span>
              <button type="button" class="algolia-range-slider-clear" data-slider-clear aria-label="Clear filter">×</button>
            </div>
          </div>
        `;

        const sliderEl = containerEl.querySelector('.algolia-range-slider');
        const track = containerEl.querySelector('[data-slider-track]');
        const thumb = containerEl.querySelector('[data-slider-thumb]');
        const fill = containerEl.querySelector('[data-slider-fill]');
        const input = containerEl.querySelector('[data-slider-input]');
        const anyLabel = containerEl.querySelector('[data-slider-any]');
        const clearBtn = containerEl.querySelector('[data-slider-clear]');

        let isDragging = false;
        let currentVal = currentValue;
        let isAnyState = !hasFilter;

        const updateSlider = (value, isAny = false) => {
          isAnyState = isAny;
          currentVal = value;
          sliderEl.dataset.hasFilter = !isAny;

          if (isAny) {
            // "Any" state - hide thumb at start, no fill
            thumb.style.left = '0%';
            fill.style.width = '0%';
            input.hidden = true;
            anyLabel.hidden = false;
            clearBtn.hidden = true;
          } else {
            // Active filter state
            const percentage = ((value - min) / (max - min)) * 100;
            thumb.style.left = `${percentage}%`;
            fill.style.width = `${percentage}%`;
            input.value = value;
            input.hidden = false;
            anyLabel.hidden = true;
            clearBtn.hidden = false;
          }
        };

        // Initialize slider position
        updateSlider(currentValue || min, !hasFilter);

        const getValueFromPosition = (clientX) => {
          const rect = track.getBoundingClientRect();
          const percentage = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
          return Math.round(min + percentage * (max - min));
        };

        const handleMove = (clientX) => {
          if (!isDragging) return;
          const value = Math.max(min, Math.min(max, getValueFromPosition(clientX)));
          updateSlider(value, false);
        };

        const handleEnd = () => {
          if (!isDragging) return;
          isDragging = false;
          // Refine with exact match (same value for min and max)
          refine([currentVal, currentVal]);
          document.removeEventListener('mousemove', onMouseMove);
          document.removeEventListener('mouseup', onMouseUp);
          document.removeEventListener('touchmove', onTouchMove);
          document.removeEventListener('touchend', onTouchEnd);
        };

        const onMouseMove = (e) => handleMove(e.clientX);
        const onMouseUp = () => handleEnd();
        const onTouchMove = (e) => handleMove(e.touches[0].clientX);
        const onTouchEnd = () => handleEnd();

        const startDrag = () => {
          isDragging = true;
          document.addEventListener('mousemove', onMouseMove);
          document.addEventListener('mouseup', onMouseUp);
        };

        const startDragTouch = () => {
          isDragging = true;
          document.addEventListener('touchmove', onTouchMove);
          document.addEventListener('touchend', onTouchEnd);
        };

        thumb.addEventListener('mousedown', (e) => {
          e.preventDefault();
          startDrag();
        });

        thumb.addEventListener('touchstart', () => {
          startDragTouch();
        }, { passive: true });

        track.addEventListener('click', (e) => {
          if (e.target === thumb) return;
          const value = Math.max(min, Math.min(max, getValueFromPosition(e.clientX)));
          updateSlider(value, false);
          refine([currentVal, currentVal]);
        });

        input.addEventListener('change', (e) => {
          let value = parseInt(e.target.value, 10);
          value = Math.max(min, Math.min(max, value));
          updateSlider(value, false);
          refine([currentVal, currentVal]);
        });

        // Clear button handler
        clearBtn.addEventListener('click', () => {
          updateSlider(min, true);
          refine([undefined, undefined]); // Clear the refinement
        });

        // Store update function for later renders
        containerEl._updateSlider = updateSlider;
        containerEl._hasFilter = hasFilter;
      }

      // Update slider position on subsequent renders
      if (containerEl._updateSlider) {
        containerEl._updateSlider(currentValue || min, !hasFilter);
      }
    };

    const customSingleSlider = connectRange(renderSingleSlider);

    return customSingleSlider({
      attribute: attribute,
    });
  }
}

customElements.define('algolia-search-results', AlgoliaSearchResults);
